import { Text, Stack, FontWeights } from '@fluentui/react';
import { CvssView } from '../Cvss';
import { VulnerabilityJumpTo } from '../VulnerabilityJumpTo';
import { ImpactSeverityDisplay } from './ImpactSeverityDisplay';
import { CVEOrgLink } from './CVEOrgLink';
import { AssigningCna } from './AssigningCna';
import { CweDetailsListValue } from './CweDetailsListValue';
import { CweList } from './CweList';
import { VectorStringSource } from './VectorStringSource';
import { DateDisplay } from './DateDisplay';
import { ICveNumber } from '../../../../../../api/apiTypes';
import { useVulnerabilitySingleApi } from '../../../../../../api/vulnerability';
import { Card } from '@coherence-design-system/controls';
import { SubscribeButtonCluster } from 'component/nav/SubscribeButtonCluster/SubscribeButtonCluster';
import { TitleCandyCluster } from './TitleCandyCluster';
import { CustomerActionRequiredTitle } from './customerActionRequiredTitle';

/** This is the title and info card for a CVE item. */
export const VulnTitleCard = ({ cveNumber }: TitleViewProps) => {
  const { data, isLoading } = useVulnerabilitySingleApi(cveNumber);

  const {
    cveTitle,
    cweList,
    cweDetailsList,
    customerActionRequired,
    issuingCna,
    releaseDate,
    impact,
    severity,
    vulnType,
    revisions,
    latestRevisionDate,
    mitreText,
    mitreUrl,
    vectorStringSource,
  } = data || {};

  return (
    // Don't set "loading" on the card, we want to render this card and show the spinner in the title.
    <Card
      styles={{
        root: {
          height: '-webkit-fit-content',
          '@media (min-width: 768px)': {
            // On this card, we want to display the dt/dd pairs inline.
            dl: {
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
            },
            dt: {
              '::after': {
                content: '":"',
              },
            },
            dd: {
              marginLeft: '0.35em',
            },
          },
        },
      }}
    >
      <Stack
        verticalAlign='center'
        tokens={{ childrenGap: '0.5rem' }}
        horizontal
        horizontalAlign='space-between'
        wrap
      >
        <div
          style={{
            flexBasis: '80%',
            minWidth: '300px',
          }}
        >
          <Text
            block
            as='h1'
            styles={{ root: { margin: '0px' } }}
            variant='xLarge'
            className='ms-fontWeight-semibold'
          >
            {cveTitle}
            <TitleCandyCluster
              isLoading={isLoading}
              latestRevisionDate={latestRevisionDate}
              revisionLength={revisions?.length || 0}
              releaseDate={releaseDate}
            />
          </Text>
        </div>
        <div style={{ fontSize: 'smaller', fontStretch: 'semi-condensed' }}>
          <VulnerabilityJumpTo isLoading={isLoading || false} />
        </div>
      </Stack>
      <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
        <Text variant='large'>{cveNumber}</Text>
        <SubscribeButtonCluster />
      </Stack>
      {vulnType && (
        <Text block variant='large'>
          {vulnType}
        </Text>
      )}
      <div style={{ fontWeight: FontWeights.bold }}>
        <p>
          <DateDisplay date={releaseDate} i18nKey='releaseDate' />
        </p>
        {revisions && revisions?.length > 1 && (
          <p>
            <DateDisplay date={latestRevisionDate} i18nKey='lastUpdated' />
          </p>
        )}
      </div>
      <AssigningCna loading={isLoading} assigningCna={issuingCna} />
      {customerActionRequired === false && (
        <CustomerActionRequiredTitle
          loading={isLoading}
          isCustomerActionRequired={customerActionRequired}
        />
      )}
      <CVEOrgLink
        cveNumber={cveNumber}
        mitreText={mitreText}
        mitreUrl={mitreUrl}
      />
      <ImpactSeverityDisplay impact={impact} severity={severity} />

      {cweDetailsList && cweDetailsList.length > 0 ? (
        <CweDetailsListValue
          loading={isLoading}
          cweDetailsList={cweDetailsList}
        />
      ) : (
        // fall back just in case cweDetailsList doesn't exist
        <CweList loading={isLoading} cweValue={cweList} />
      )}
      <VectorStringSource vectorStringSource={vectorStringSource} />
      <CvssView cveNumber={cveNumber} />
    </Card>
  );
};

export interface TitleViewProps extends ICveNumber {}
